import { useState } from "react"
import { ArrowUpRight, HelpCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button, type buttonVariants } from "@/shared/components/ui/button"
import type { VariantProps } from "class-variance-authority"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover"
import { cn } from "@/shared/lib/utils"
import {
  collectTips,
  type ConfigHelpParams,
  type ConfigSectionKey,
} from "./config-help-registry"
import { useConfigHelpEnabled, useOpenConfig } from "./use-config-help"

interface ConfigHelpProps {
  /**
   * The config sections relevant to what is currently on screen. On a wizard
   * this is the active step's sections; on a list/detail surface it is a small
   * fixed set (keep it to 3 or fewer).
   */
  sections: ConfigSectionKey[]
  /** Label for the surface the user is on now (shown in the return pill). */
  returnLabel: string
  /**
   * Resolved record ids for the surface on screen. Tips whose exact config
   * location needs one of these (e.g. the specific warranty template's Display
   * tab) build their route from here; a tip whose required id is missing is
   * dropped, so the button never links to a non-exact location.
   */
  params?: ConfigHelpParams
  className?: string
  /**
   * Button size. Defaults to the compact inline "icon-xs" used next to
   * wizard/list headings; detail pages pass a larger size (e.g. "icon") so the
   * circular help button matches the other header action buttons.
   */
  size?: VariantProps<typeof buttonVariants>["size"]
}

/**
 * The circled "?" help affordance. Opens a minimal popover listing where the
 * on-screen surface is configured: one tip in the common case, a compact
 * summary of the current step's configs on the wizards. Each tip links to the
 * dedicated config page and records where to return to. Renders nothing for
 * users without config-help access, or when there are no tips.
 */
export function ConfigHelp({
  sections = [],
  returnLabel,
  params,
  className,
  size = "icon-xs",
}: ConfigHelpProps) {
  const { t } = useTranslation()
  const enabled = useConfigHelpEnabled()
  const openConfig = useOpenConfig()
  const [open, setOpen] = useState(false)

  const tips = collectTips(sections, params)
  if (!enabled || tips.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size={size}
            className={cn("rounded-full text-[var(--ink-2)]", className)}
            aria-label={t("configHelp.help", { defaultValue: "How is this configured?" })}
            title={t("configHelp.help", { defaultValue: "How is this configured?" })}
          >
            <HelpCircle aria-hidden />
          </Button>
        }
      />
      <PopoverContent side="bottom" align="end" className="max-w-[320px]">
        <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
          {t("configHelp.title", { defaultValue: "How this is configured" })}
        </p>
        <ul className="flex flex-col gap-3">
          {tips.map((tip) => (
            <li key={`${tip.route}:${tip.title}`} className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-[var(--ink-1)]">
                {tip.title}
              </span>
              <span className="text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
                {tip.paragraph}
              </span>
              <Button
                variant="link"
                size="xs"
                className="h-auto justify-start px-0 text-[var(--green)]"
                onClick={() => {
                  setOpen(false)
                  openConfig(tip.route, returnLabel)
                }}
              >
                {tip.label}
                <ArrowUpRight aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
